import React from 'react';

import {ExtractedCallData} from '../../charts/types';
import {
  getUsageInputTokens,
  getUsageOutputTokens,
} from '../CallPage/cost/costUtils';
import {getCostFromCostData} from '../CallPage/cost/TraceCostStats';
import {CallSchema} from '../wfReactInterface/wfDataModelHooksInterface';

type CallsSummaryContextValue = {
  data: ExtractedCallData[];
  visibleCallIds: string[];
};

const CallsSummaryContext =
  React.createContext<CallsSummaryContextValue | null>(null);

export const useCallsSummaryContext = (): CallsSummaryContextValue | null => {
  return React.useContext(CallsSummaryContext);
};

export const CallsSummaryProvider: React.FC<{
  calls: CallSchema[];
  visibleRowIds: string[];
}> = ({calls, visibleRowIds, children}) => {
  const data: ExtractedCallData[] = React.useMemo(() => {
    const result: ExtractedCallData[] = [];
    for (const call of calls) {
      const tc = call.traceCall;
      const latencyMs = tc.summary?.weave?.latency_ms;
      const costsMap = tc.summary?.weave?.costs;
      const usageMap = (tc.summary as any)?.usage as
        | {[key: string]: any}
        | undefined;

      // Aggregate tokens across all usage entries
      let promptTokens = 0;
      let completionTokens = 0;
      if (usageMap) {
        for (const model of Object.keys(usageMap)) {
          const usage = usageMap[model];
          promptTokens += getUsageInputTokens(usage) || 0;
          completionTokens += getUsageOutputTokens(usage) || 0;
        }
      }

      const {costNum} = getCostFromCostData(costsMap);

      result.push({
        callId: tc.id,
        traceId: tc.trace_id,
        started_at: tc.started_at,
        ended_at: tc.ended_at,
        latency:
          typeof latencyMs === 'number' && !isNaN(latencyMs)
            ? latencyMs / 1000
            : undefined,
        exception: tc.exception ? 1 : undefined,
        op_name: tc.op_name,
        display_name: tc.display_name ?? undefined,
        cost: typeof costNum === 'number' ? costNum : undefined,
        prompt_tokens: promptTokens || undefined,
        completion_tokens: completionTokens || undefined,
        attributes: (tc.attributes as any) ?? undefined,
        inputs: (tc.inputs as any) ?? undefined,
        output: (tc.output as any) ?? undefined,
      });
    }
    return result;
  }, [calls]);

  const value = React.useMemo(
    () => ({data, visibleCallIds: visibleRowIds}),
    [data, visibleRowIds]
  );

  return (
    <CallsSummaryContext.Provider value={value}>
      {children}
    </CallsSummaryContext.Provider>
  );
};

export default CallsSummaryContext;
